import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { Badge, Button, EditPane, useConfirm, type EditPaneTab } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ProductClient,
  RANK_SCOPES,
  rankText,
  scopedRank,
  WarehouseClient,
  type ProductDTO,
  type WarehouseDTO,
} from "../api/product";
import { DataTab } from "./product/DataTab";
import { DispatchWarehousesTab } from "./product/DispatchWarehousesTab";
import { InventoryRulesTab } from "./product/InventoryRulesTab";
import { BundlesTab } from "./product/BundlesTab";
import { ItemsTab } from "./product/ItemsTab";
import { MetafieldsTab } from "./product/MetafieldsTab";
import { ShopifyTab } from "./product/ShopifyTab";
import { BomTab, ContractPriceTab } from "./product/referenceTabs";
import { ImagesTab } from "./product/ImagesTab";
import { AssemblyTab } from "./product/AssemblyTab";
import { InventoryTab } from "./product/InventoryTab";
import { ManufacturingTab } from "./product/ManufacturingTab";
import { InvoicesTab } from "./product/InvoicesTab";
import { SalesTab } from "./product/SalesTab";
import { OverviewTab } from "./product/OverviewTab";
import { CogsTab, MeasurementsTab, NotesTab } from "./product/tabs";
import { VariationsTab } from "./product/VariationsTab";
import { NexpimView } from "../views";
import { ProductSyncDialog } from "./configurator/ProductSyncDialog";

/**
 * The product editor.
 *
 * The Vaadin original was a seventeen-tab pane that replaced the grid in place.
 * This is a route instead, so a product is linkable and the back button works —
 * a detail view you can send someone is worth more than one you have to
 * describe how to reach.
 *
 * Tabs land incrementally; the ones still to come say so rather than pretending
 * to be empty.
 */
export function ProductDetailPage({ productId }: { productId: number }) {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const client = useMemo(() => new ProductClient(http), [http]);
  const warehouseClient = useMemo(() => new WarehouseClient(http), [http]);
  const allowed = hasView(NexpimView.PRODUCTS);

  const { data, isLoading, error } = useQuery({
    queryKey: ["product", productId],
    queryFn: () => client.findById(productId),
    enabled: allowed,
  });

  // Warehouses change rarely and every product page needs them, so this is
  // cached long and shared across visits.
  const { data: warehouses } = useQuery({
    queryKey: ["warehouse", "all"],
    queryFn: () => warehouseClient.findAll(),
    enabled: allowed,
    staleTime: 30 * 60_000,
  });

  // Counts for the Variations and Bundles tab badges. Same keys as the tabs'
  // own queries, so this is one fetch shared through the cache, not a second
  // request; and the tab and its badge can never disagree.
  const group = data?.variation_group ?? null;
  const { data: variationGroup } = useQuery({
    queryKey: ["product", "variation-group", group],
    queryFn: () => client.byVariationGroup(group as number),
    enabled: allowed && group !== null,
  });
  const { data: bundlesContaining } = useQuery({
    queryKey: ["product", "bundles-containing", productId],
    queryFn: () => client.bundlesContaining(productId),
    enabled: allowed && Boolean(data) && data?.type !== "BUNDLE",
  });

  // The draft is the edit buffer: every tab mutates it, and nothing reaches the
  // server until Save — the same buffered model the inline row editor uses.
  const [draft, setDraft] = useState<ProductDTO | null>(null);
  const [syncing, setSyncing] = useState(false);
  useEffect(() => {
    if (data) setDraft(structuredClone(data) as ProductDTO);
  }, [data]);

  const dirty = useMemo(
    () => Boolean(draft && data && JSON.stringify(draft) !== JSON.stringify(data)),
    [draft, data],
  );

  const save = useMutation({
    mutationFn: (product: ProductDTO) => client.updateProduct(product),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["product", productId] });
      await queryClient.invalidateQueries({ queryKey: ["product", "lazy", "all"] });
      toast.success(t("notification.saved", "Saved"));
    },
    onError: (failure) =>
      toast.error(failure instanceof Error ? failure.message : t("error.generic", "Save failed")),
  });

  /**
   * Answers the header's "may I leave?" — it does the navigating itself, so
   * this only decides. Returning false keeps the user on the page.
   */
  const leave = async (): Promise<boolean> => {
    if (!dirty) return true;
    return confirm({
      title: t("product.leave.title", "Discard unsaved changes?"),
      message: t("product.leave.message", "Your edits to this product have not been saved."),
      variant: "danger",
      confirmLabel: t("product.leave.discard", "Discard"),
    });
  };

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.products", "Products")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  if (isLoading || !draft) {
    return (
      <>
        <PageHeader title={t("navigation.item.products", "Products")} />
        <div className="flex h-40 items-center justify-center">
          <span className="size-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
        </div>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <PageHeader title={t("navigation.item.products", "Products")} />
        <div className="p-4 lg:p-6">
          <p className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
            {t("product.not-found", "That product could not be loaded.")}
          </p>
        </div>
      </>
    );
  }

  const tabs: EditPaneTab[] = [
    {
      id: "overview",
      label: t("product.tab.overview", "Overview"),
      content: <OverviewTab draft={draft} warehouses={warehouses ?? []} />,
    },
    {
      id: "data",
      label: t("product.tab.data", "Data"),
      content: (
        <DataTab
          draft={draft}
          onChange={setDraft}
          readOnly={!canWrite}
          onPersisted={() => void queryClient.invalidateQueries({ queryKey: ["product", productId] })}
        />
      ),
    },
    {
      id: "images",
      label: t("product.tab.images", "Images"),
      badge: draft.images?.length ? <Badge size="sm">{draft.images.length}</Badge> : null,
      content: (
        <ImagesTab
          draft={draft}
          onChange={setDraft}
          readOnly={!canWrite}
          onPersisted={() =>
            void queryClient.invalidateQueries({ queryKey: ["product", productId] })
          }
        />
      ),
    },
    {
      id: "variations",
      label: t("product.tab.variations", "Variations"),
      badge: variationGroup?.length ? <Badge size="sm">{variationGroup.length}</Badge> : null,
      content: <VariationsTab draft={draft} />,
    },
    {
      id: "items",
      label: t("product.tab.items", "Items"),
      // Only bundles have component lines, exactly as the Vaadin pane did.
      hidden: draft.type !== "BUNDLE",
      badge: draft.group_lines?.length ? <Badge size="sm">{draft.group_lines.length}</Badge> : null,
      content: (
        <ItemsTab
          draft={draft}
          readOnly={!canWrite}
          onPersisted={() =>
            void queryClient.invalidateQueries({ queryKey: ["product", productId] })
          }
        />
      ),
    },
    {
      id: "bundles",
      label: t("product.tab.bundles", "Bundles"),
      // The mirror of "Items": a bundle lists what is in it, an item lists what
      // it is in. A bundle inside a bundle is not a thing here, so this is
      // hidden for bundles rather than showing a permanently empty tab.
      hidden: draft.type === "BUNDLE",
      badge: bundlesContaining?.length ? <Badge size="sm">{bundlesContaining.length}</Badge> : null,
      content: <BundlesTab draft={draft} />,
    },
    {
      id: "measurements",
      label: t("product.tab.measurements", "Measurements"),
      content: <MeasurementsTab draft={draft} onChange={setDraft} readOnly={!canWrite} />,
    },
    {
      id: "cogs",
      flush: true,
      label: t("product.tab.cogs", "COGS"),
      // Bundles have no cost run of their own — their COGS rolls up from items.
      hidden: draft.type === "BUNDLE",
      content: <CogsTab draft={draft} onChange={setDraft} readOnly={!canWrite} />,
    },
    {
      id: "inventory-rules",
      flush: true,
      label: t("product.tab.inventory-rules", "Inventory rules"),
      content: <InventoryRulesTab draft={draft} onChange={setDraft} readOnly={!canWrite} />,
    },
    {
      id: "contract-price",
      flush: true,
      label: t("product.tab.contract-price", "Contract price"),
      content: <ContractPriceTab draft={draft} />,
    },
    // Manufacturing reads the SIGM replacement, still dark for most accounts
    // (docs/sigm-mfi-replacement/dark-launch.md); they keep SIGM's BOM tab.
    {
      id: "fabrication",
      flush: true,
      label: t("product.tab.fabrication", "Fabrication"),
      hidden: !hasView(NexpimView.MANUFACTURING_PARTS),
      content: <ManufacturingTab key="fabrication" draft={draft} side="fabrication" />,
    },
    {
      id: "assembly",
      flush: true,
      label: t("product.tab.plant-assembly", "Assembly"),
      hidden: !hasView(NexpimView.MANUFACTURING_PARTS),
      content: <AssemblyTab draft={draft} />,
    },
    {
      id: "boxed-stock",
      flush: true,
      label: t("product.tab.boxed-stock", "Boxed stock"),
      hidden: !hasView(NexpimView.MANUFACTURING_PARTS),
      content: <InventoryTab draft={draft} />,
    },
    {
      id: "bom",
      flush: true,
      label: t("product.tab.bom", "BOM"),
      hidden: draft.type === "BUNDLE" || hasView(NexpimView.MANUFACTURING_PARTS),
      content: <BomTab draft={draft} />,
    },
    {
      id: "dispatch",
      label: t("product.tab.dispatch", "Warehouses"),
      content: <DispatchWarehousesTab draft={draft} onChange={setDraft} readOnly={!canWrite} />,
    },
    {
      id: "metafields",
      flush: true,
      label: t("product.tab.metafields", "Metafields"),
      content: <MetafieldsTab draft={draft} />,
    },
    {
      id: "shopify",
      label: t("product.tab.shopify", "Shopify"),
      badge: draft.shopify_excluded ? <Badge size="sm" variant="warning">{t("shopify.status.excluded", "Excluded")}</Badge> : null,
      content: <ShopifyTab draft={draft} readOnly={!canWrite} />,
    },
    { id: "sales", label: t("product.tab.sales", "Sales"), content: <SalesTab draft={draft} /> },
    {
      id: "invoices",
      flush: true,
      label: t("product.tab.invoices", "Invoices"),
      content: <InvoicesTab draft={draft} />,
    },
    {
      id: "notes",
      label: t("product.tab.notes", "Notes"),
      badge: draft.notes?.length ? <Badge size="sm">{draft.notes.length}</Badge> : null,
      content: <NotesTab draft={draft} onChange={setDraft} readOnly={!canWrite} />,
    },
  ];

  return (
    <>
      <PageHeader
        parent={{
          to: "/products",
          label: t("navigation.item.products", "Products"),
          beforeLeave: leave,
        }}
        title={draft.metadata?.sku ?? t("navigation.item.products", "Products")}
        subtitle={draft.description_en?.product_name ?? ""}
        badges={
          <>
            <Badge variant={draft.status === "AVAILABLE" ? "success" : "warning"} size="sm">
              {draft.status}
            </Badge>
            {dirty && (
              <Badge variant="warning" size="sm">
                {t("product.unsaved", "Unsaved")}
              </Badge>
            )}
          </>
        }
        actions={
          <>
            {canWrite && hasView(NexpimView.MANUFACTURING_PARTS) && draft.id > 0 && draft.type !== "BUNDLE" && (
              <Button variant="secondary" onClick={() => setSyncing(true)}>
                {t("sync.open", "Sync colours")}
              </Button>
            )}
            {canWrite && (
              <Button
                variant="primary"
                disabled={!dirty}
                loading={save.isPending}
                onClick={() => save.mutate(draft)}
              >
                {t("button.save", "Save")}
              </Button>
            )}
          </>
        }
      />

      <EditPane tabs={tabs} urlParam="tab" header={<KpiStrip draft={draft} warehouses={warehouses ?? []} />} />
      {syncing && <ProductSyncDialog productId={draft.id} onClose={() => setSyncing(false)} />}
    </>
  );
}

/**
 * Inventory and sales at a glance, the `ProductKpiStatsBlock` equivalent.
 *
 * Available quantity is split by warehouse rather than shown as one number:
 * stock in Canada does not help a US order, so a single total hides the
 * question people are actually asking.
 */
function KpiStrip({ draft, warehouses }: { draft: ProductDTO; warehouses: WarehouseDTO[] }) {
  const { t } = useTranslation();
  const inventory = draft.kpi_inventory;
  const sales = draft.kpi_sales;

  // The KPI map is keyed by warehouse id; the business identifies warehouses by
  // code, so resolve through the warehouse list rather than hard-coding ids.
  const byCode = (code: string) => {
    const warehouse = warehouses.find((entry) => entry.code === code);
    if (!warehouse) return null;
    return inventory?.warehouse_quantities?.[String(warehouse.id)] ?? null;
  };

  const canada = byCode("NEXCA");
  const us = byCode("NEXUS");

  // Available is what is on hand minus what is already spoken for.
  const available = (entry: { quantity: number; reserved_quantity: number } | null) =>
    entry ? entry.quantity - entry.reserved_quantity : null;

  return (
    <dl className="flex flex-wrap gap-x-8 gap-y-3">
      {/* Global, Canada and US rank side by side, each over its own weekly
          velocity, set off from the other figures by a hairline. */}
      <div className="flex gap-x-6 border-r border-line pr-8">
        {RANK_SCOPES.map((scope) => {
          const { rank, velocity } = scopedRank(draft, scope);
          return (
            <Figure
              key={scope}
              label={
                scope === "GLOBAL"
                  ? t("product.kpi.rank-global", "Global rank")
                  : scope === "CA"
                    ? t("product.kpi.rank-ca", "Canada rank")
                    : t("product.kpi.rank-us", "US rank")
              }
              value={rankText(rank, draft.type, t) ?? ""}
              note={t("product.kpi.velocity", "{{velocity}} / week", { velocity: num(velocity) })}
            />
          );
        })}
      </div>
      <Figure label={t("product.kpi.ytd-ca", "YTD units CA")} value={num(sales?.ytd_units_cad)} />
      <Figure label={t("product.kpi.ytd-us", "YTD units US")} value={num(sales?.ytd_units_usd)} />
      <Figure
        label={t("product.kpi.available", "Available")}
        value={num(available(canada))}
        note="NEXCA"
        secondValue={num(available(us))}
        secondNote="NEXUS"
      />
      <Figure
        label={t("product.kpi.in-production", "In production")}
        value={num(inventory?.production_quantity)}
      />
      <Figure label={t("column.cogs", "COGS")} value={num(draft.pricing?.cogs)} />
    </dl>
  );
}

function Figure({
  label,
  value,
  note,
  secondValue,
  secondNote,
}: {
  label: string;
  value: string;
  note?: string;
  secondValue?: string;
  secondNote?: string;
}) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-ink-faint">{label}</dt>
      <dd className="mt-0.5 flex items-baseline gap-3">
        <span className="tabular text-sm font-semibold text-ink">
          {value}
          {note && <span className="ml-1 text-[11px] font-normal text-ink-faint">{note}</span>}
        </span>
        {secondValue !== undefined && (
          <span className="tabular text-sm font-semibold text-ink">
            {secondValue}
            {secondNote && (
              <span className="ml-1 text-[11px] font-normal text-ink-faint">{secondNote}</span>
            )}
          </span>
        )}
      </dd>
    </div>
  );
}


function num(value: number | null | undefined): string {
  return value === null || value === undefined ? "" : new Intl.NumberFormat().format(value);
}


